import { AccessControlCode } from '@shared/constants';
import { UserRole } from '@shared/types';
import {
  AlertTriangle,
  ArrowRightLeft,
  BarChart2,
  BedDouble,
  BookMarked,
  BookOpen,
  Building2,
  Calculator,
  CalendarDays,
  CalendarPlus2,
  CheckCircle2,
  ClipboardList,
  Cpu,
  CreditCard,
  FileText,
  Grid2X2,
  LayoutDashboard,
  LucideIcon,
  Mail,
  Menu,
  MoonStar,
  Package2,
  PackageSearch,
  PackageX,
  Printer,
  ReceiptText,
  RotateCw,
  Settings,
  ShieldCheck,
  ShoppingBasket,
  SlidersHorizontal,
  Star,
  Store,
  Truck,
  User as UserIcon,
  Users,
} from 'lucide-react';

export interface SidebarLinkConfig {
  id: string;
  label: string;
  icon: LucideIcon | null;
  to?: string;
  activeMatch?: 'exact' | 'prefix';
  children?: SidebarLinkConfig[];
  requiredControl?: AccessControlCode;
  allowedRoles?: readonly UserRole[];
  /** Only render this link when the current company has RRA fiscal
   *  integration enabled (has_fiscal_integration) — used to hide the EBM
   *  section for non-Rwanda companies. */
  requiresFiscalIntegration?: boolean;
}

export interface SidebarSectionConfig {
  id: string;
  title?: string;
  links: SidebarLinkConfig[];
}

// ----------------------------------------------------------------
// Dashboard sections
// ----------------------------------------------------------------
export const DASHBOARD_SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'main',
    links: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/dashboard',
        requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
      },
    ],
  },
  {
    id: 'operations',
    title: 'Operations',
    links: [
      {
        id: 'menu',
        label: 'Menu',
        icon: Menu,
        children: [
          {
            id: 'departments',
            label: 'Departments',
            icon: null,
            to: '/dashboard/departments',
            requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
          },
          {
            id: 'menu-items',
            label: 'Menu Items',
            icon: null,
            to: '/dashboard/menu',
            requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
          },
        ],
      },
      {
        id: 'sections-tables',
        label: 'Sections & Tables',
        icon: Grid2X2,
        to: '/dashboard/sections-tables',
        requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
      },
    ],
  },
  {
    id: 'management',
    title: 'Management',
    links: [
      {
        id: 'outlets',
        label: 'Outlets',
        icon: Store,
        to: '/dashboard/outlets',
        requiredControl: AccessControlCode.DASHBOARD_PAGE_ACCESS,
      },
      {
        id: 'clients',
        label: 'Clients',
        icon: UserIcon,
        to: '/dashboard/clients',
        requiredControl: AccessControlCode.DASHBOARD_CLIENTS_VIEW,
      },
      {
        id: 'reports',
        label: 'Sales Reports',
        icon: FileText,
        children: [
          {
            id: 'orders-report',
            label: 'Orders',
            icon: null,
            to: '/dashboard/reports/orders',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'settlements-report',
            label: 'Settlements',
            icon: null,
            to: '/dashboard/reports/settlements',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'daily-sales-report',
            label: 'Daily sales',
            icon: null,
            to: '/dashboard/reports/daily-sales',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'payment-methods-report',
            label: 'Sales Summary',
            icon: null,
            to: '/dashboard/reports/payment-methods',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'departments-report',
            label: 'Department Sales',
            icon: null,
            to: '/dashboard/reports/departments',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'items-report',
            label: 'Items sold',
            icon: null,
            to: '/dashboard/reports/items',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'discounts-report',
            label: 'Discounts',
            icon: null,
            to: '/dashboard/reports/discounts',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'creditors-report',
            label: 'Creditors & NC',
            icon: null,
            to: '/dashboard/reports/creditors',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'kot-tracking-report',
            label: 'KOT Tracking',
            icon: null,
            to: '/dashboard/reports/kot-tracking',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
          {
            id: 'credit-bills',
            label: 'Credit Bills',
            icon: null,
            to: '/dashboard/reports/credit-bills',
            requiredControl: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
          },
        ],
      },
    ],
  },
];

// ----------------------------------------------------------------
// Accommodation sections
// ----------------------------------------------------------------
export const ACCOMMODATION_SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'header',
    links: [
      {
        id: 'accommodation-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/accommodation/dashboard',
        requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
      },
    ],
  },

  {
    id: 'daily-operation',
    title: 'Daily Operation',
    links: [
      {
        id: 'accommodation-reservations',
        label: 'Reservations',
        icon: CalendarDays,
        requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
        children: [
          {
            id: 'accommodation-all-bookings',
            label: 'All Bookings',
            icon: CalendarDays,
            to: '/accommodation/bookings',
            requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
          },
          {
            id: 'accommodation-new-booking',
            label: 'New Booking',
            icon: CalendarPlus2,
            to: '/accommodation/bookings/new',
            requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
          },
        ],
      },
      {
        id: 'accommodation-room-operations',
        label: 'Rooms',
        icon: BedDouble,
        to: '/accommodation/rooms',
        requiredControl: AccessControlCode.ACCOMMODATION_ROOMS_MANAGE,
      },
      {
        id: 'accommodation-guests',
        label: 'Guests',
        icon: Users,
        to: '/accommodation/guests',
        requiredControl: AccessControlCode.DASHBOARD_CLIENTS_VIEW,
      },
      {
        id: 'accommodation-payments',
        label: 'Payments',
        icon: CreditCard,
        to: '/accommodation/payments',
        requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
      },
      {
        id: 'accommodation-ota-payouts',
        label: 'OTA Payouts',
        icon: ArrowRightLeft,
        to: '/accommodation/ota-payouts',
        requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
      },
      {
        id: 'accommodation-overstays',
        label: 'Overstays',
        icon: AlertTriangle,
        to: '/accommodation/overstays',
        requiredControl: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
      },
    ],
  },
  {
    id: 'accounting',
    title: 'Reports',
    links: [
      {
        id: 'accommodation-reports',
        label: 'Reports',
        icon: BarChart2,
        to: '/accommodation/reports',
        requiredControl: AccessControlCode.ACCOMMODATION_REPORTS_VIEW,
        children: [
          {
            id: 'accommodation-revenue-report',
            label: 'Revenue',
            icon: BarChart2,
            to: '/accommodation/reports/revenue',
            requiredControl: AccessControlCode.ACCOMMODATION_REPORTS_REVENUE,
          },
          {
            id: 'accommodation-occupancy-report',
            label: 'Occupancy',
            icon: Building2,
            to: '/accommodation/reports/occupancy',
            requiredControl: AccessControlCode.ACCOMMODATION_REPORTS_OCCUPANCY,
          },
          {
            id: 'accommodation-booking-source-report',
            label: 'Booking Sources',
            icon: BookMarked,
            to: '/accommodation/reports/booking-source',
            requiredControl:
              AccessControlCode.ACCOMMODATION_REPORTS_BOOKING_SOURCE,
          },
          {
            id: 'accommodation-night-audit-report',
            label: 'Night Audit',
            icon: MoonStar,
            to: '/accommodation/reports/night-audit',
            requiredControl:
              AccessControlCode.ACCOMMODATION_REPORTS_NIGHT_AUDIT,
          },
          {
            id: 'accommodation-room-revenue-report',
            label: 'Room Revenue',
            icon: Grid2X2,
            to: '/accommodation/reports/room-revenue',
            requiredControl:
              AccessControlCode.ACCOMMODATION_REPORTS_ROOM_REVENUE,
          },
          {
            id: 'accommodation-revpar-report',
            label: 'RevPAR',
            icon: BarChart2,
            to: '/accommodation/reports/revpar',
            requiredControl: AccessControlCode.ACCOMMODATION_REPORTS_REVPAR,
          },
        ],
      },
      {
        id: 'accommodation-reviews',
        label: 'Reviews',
        icon: Star,
        to: '/accommodation/reviews',
        requiredControl: AccessControlCode.ACCOMMODATION_REVIEWS_VIEW,
      },
    ],
  },
  {
    id: 'system-options',
    title: 'System Options',
    links: [
      {
        id: 'accommodation-settings',
        label: 'Settings',
        icon: Settings,
        requiredControl: AccessControlCode.ACCOMMODATION_SETTINGS_MANAGE,
        children: [
          {
            id: 'accommodation-properties',
            label: 'Properties',
            icon: Building2,
            to: '/accommodation/settings/properties',
            requiredControl: AccessControlCode.ACCOMMODATION_SETTINGS_MANAGE,
          },
          {
            id: 'accommodation-rate-plans',
            label: 'Rate Plans',
            icon: null,
            to: '/accommodation/settings/rate-plans',
            requiredControl: AccessControlCode.ACCOMMODATION_SETTINGS_MANAGE,
          },
        ],
      },
    ],
  },
];

// ----------------------------------------------------------------
// Accounting sections
// ----------------------------------------------------------------
export const ACCOUNTING_SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'header',
    links: [
      {
        id: 'accounting-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/accounting/dashboard',
        requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
      },
      {
        id: 'accounting-guide',
        label: 'Guide',
        icon: BookOpen,
        to: '/accounting/guide',
      },
    ],
  },
  {
    id: 'accounting-workspace',
    title: 'Workspace',
    links: [
      {
        id: 'accounting-chart-of-accounts',
        label: 'Chart of Accounts',
        icon: BookMarked,
        to: '/accounting/chart-of-accounts',
        requiredControl: AccessControlCode.ACCOUNTING_VIEW_ACCOUNTS,
      },
      {
        id: 'accounting-journals',
        label: 'Journal Entries',
        icon: ReceiptText,
        to: '/accounting/journals',
        requiredControl: AccessControlCode.ACCOUNTING_VIEW_JOURNALS,
      },
      {
        id: 'accounting-payables',
        label: 'Payables',
        icon: Calculator,
        to: '/accounting/payables',
        requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
      },
    ],
  },
  {
    id: 'accounting-insights',
    title: 'Insights',
    links: [
      {
        id: 'accounting-reports',
        label: 'Reports',
        icon: BarChart2,
        to: '/accounting/reports',
        requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
        children: [
          {
            id: 'accounting-report-trial-balance',
            label: 'Trial Balance',
            icon: null,
            to: '/accounting/reports/trial-balance',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-general-ledger',
            label: 'General Ledger',
            icon: null,
            to: '/accounting/reports/general-ledger',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-account-activity',
            label: 'Account Activity',
            icon: null,
            to: '/accounting/reports/account-activity',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-pnl',
            label: 'Profit & Loss',
            icon: null,
            to: '/accounting/reports/profit-and-loss',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-tax',
            label: 'Tax / VAT',
            icon: null,
            to: '/accounting/reports/tax-report',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-cogs',
            label: 'COGS',
            icon: null,
            to: '/accounting/reports/cogs-report',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-deferred',
            label: 'Deferred Revenue',
            icon: null,
            to: '/accounting/reports/deferred-revenue',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
          {
            id: 'accounting-report-expenses',
            label: 'Expenses',
            icon: null,
            to: '/accounting/reports/expenses',
            requiredControl: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
          },
        ],
      },
    ],
  },
  {
    id: 'accounting-options',
    title: 'System Options',
    links: [
      {
        id: 'accounting-settings',
        label: 'Settings',
        icon: Settings,
        children: [
          {
            id: 'accounting-settings-tax-rates',
            label: 'Tax Rates',
            icon: null,
            to: '/accounting/settings/tax-rates',
            requiredControl: AccessControlCode.ACCOUNTING_MANAGE_TAX_RATES,
          },
          {
            id: 'accounting-settings-posting-rules',
            label: 'Posting Rules',
            icon: null,
            to: '/accounting/settings',
            activeMatch: 'exact',
            requiredControl:
              AccessControlCode.ACCOUNTING_MANAGE_PAYMENT_MAPPINGS,
          },
          {
            id: 'accounting-settings-payment-mappings',
            label: 'Payment Mappings',
            icon: null,
            to: '/accounting/settings/payment-mappings',
            requiredControl:
              AccessControlCode.ACCOUNTING_MANAGE_PAYMENT_MAPPINGS,
          },
          {
            id: 'accounting-settings-periods',
            label: 'Accounting Periods',
            icon: null,
            to: '/accounting/settings/accounting-periods',
            requiredControl: AccessControlCode.ACCOUNTING_CLOSE_PERIODS,
          },
          {
            id: 'accounting-settings-tax-refunds',
            label: 'Tax Refunds',
            icon: null,
            to: '/accounting/settings/tax-refunds',
            requiredControl: AccessControlCode.ACCOUNTING_MANAGE_TAX_RATES,
          },
        ],
      },
    ],
  },
];

// ----------------------------------------------------------------
// Inventory sections
// ----------------------------------------------------------------
export const INVENTORY_SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'header',
    links: [
      {
        id: 'inventory-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/inventory/dashboard',
        requiredControl: AccessControlCode.INVENTORY_PAGE_ACCESS,
      },
    ],
  },
  {
    id: 'inventory-operations',
    title: 'Operations',
    links: [
      {
        id: 'inventory-items',
        label: 'Items',
        icon: PackageSearch,
        requiredControl: AccessControlCode.INVENTORY_ITEMS_VIEW,
        children: [
          {
            id: 'inventory-items-list',
            label: 'Item List',
            icon: null,
            to: '/inventory/items/list',
            requiredControl: AccessControlCode.INVENTORY_ITEMS_VIEW,
          },
          {
            id: 'inventory-items-categories',
            label: 'Categories',
            icon: null,
            to: '/inventory/items/categories',
            requiredControl: AccessControlCode.INVENTORY_ITEMS_VIEW,
          },
          {
            id: 'inventory-items-units',
            label: 'Units',
            icon: null,
            to: '/inventory/items/units',
            requiredControl: AccessControlCode.INVENTORY_ITEMS_VIEW,
          },
        ],
      },
      {
        id: 'inventory-stock',
        label: 'Stock',
        icon: Package2,
        requiredControl: AccessControlCode.INVENTORY_STOCK_VIEW,
        children: [
          {
            id: 'inventory-stock-locations',
            label: 'Locations',
            icon: null,
            to: '/inventory/stock/locations',
            requiredControl: AccessControlCode.INVENTORY_STOCK_VIEW,
          },
          {
            id: 'inventory-stock-balances',
            label: 'Balances',
            icon: null,
            to: '/inventory/stock/balances',
            requiredControl: AccessControlCode.INVENTORY_STOCK_VIEW,
          },
          {
            id: 'inventory-stock-movements',
            label: 'Movements',
            icon: null,
            to: '/inventory/stock/movements',
            requiredControl: AccessControlCode.INVENTORY_STOCK_VIEW,
          },
          {
            id: 'inventory-report-movement-summary',
            label: 'Movement Summary',
            icon: null,
            to: '/inventory/reports/movement-summary',
            requiredControl: AccessControlCode.INVENTORY_REPORTS_VIEW,
          },
          {
            id: 'inventory-stock-alerts',
            label: 'Running Low',
            icon: null,
            to: '/inventory/stock/low-stock',
            requiredControl: AccessControlCode.INVENTORY_STOCK_VIEW,
          },
        ],
      },
      {
        id: 'inventory-purchases',
        label: 'Purchases',
        icon: ShoppingBasket,
        requiredControl: AccessControlCode.INVENTORY_INVOICES_VIEW,
        children: [
          {
            id: 'inventory-purchases-list',
            label: 'Purchases',
            icon: null,
            to: '/inventory/purchases',
            requiredControl: AccessControlCode.INVENTORY_INVOICES_VIEW,
          },
          {
            id: 'inventory-purchases-payment-history',
            label: 'Payment History',
            icon: null,
            to: '/inventory/purchases/payment-history',
            requiredControl: AccessControlCode.INVENTORY_INVOICES_VIEW,
          },
        ],
      },
      {
        id: 'inventory-suppliers',
        label: 'Suppliers',
        icon: Truck,
        to: '/inventory/suppliers',
        requiredControl: AccessControlCode.INVENTORY_SUPPLIERS_VIEW,
      },
      {
        id: 'inventory-recipes',
        label: 'Recipes',
        icon: SlidersHorizontal,
        to: '/inventory/recipes',
        requiredControl: AccessControlCode.INVENTORY_RECIPES_VIEW,
      },
      {
        id: 'inventory-missing-recipes',
        label: 'Missing Recipes',
        icon: AlertTriangle,
        to: '/inventory/missing',
        requiredControl: AccessControlCode.INVENTORY_RECIPES_VIEW,
      },
      {
        id: 'inventory-transfers',
        label: 'Transfers',
        icon: ArrowRightLeft,
        to: '/inventory/transfers',
        requiredControl: AccessControlCode.INVENTORY_STOCK_TRANSFER,
      },
      {
        id: 'inventory-adjustments',
        label: 'Adjustments',
        icon: FileText,
        to: '/inventory/adjustments',
        requiredControl: AccessControlCode.INVENTORY_STOCK_ADJUST,
      },
      {
        id: 'inventory-returns',
        label: 'Return to Supplier',
        icon: PackageX,
        to: '/inventory/returns',
        requiredControl: AccessControlCode.INVENTORY_STOCK_ADJUST,
      },
      {
        id: 'inventory-stock-count',
        label: 'Stock Count',
        icon: ClipboardList,
        to: '/inventory/stock-count',
        requiredControl: AccessControlCode.INVENTORY_STOCK_ADJUST,
      },
    ],
  },
  {
    id: 'inventory-reporting',
    title: 'Insights',
    links: [
      {
        id: 'inventory-reports',
        label: 'Reports',
        icon: BarChart2,
        requiredControl: AccessControlCode.INVENTORY_REPORTS_VIEW,
        children: [
          {
            id: 'inventory-report-movement-history',
            label: 'Movement History',
            icon: null,
            to: '/inventory/reports/movement-history',
            requiredControl: AccessControlCode.INVENTORY_REPORTS_VIEW,
          },
          {
            id: 'inventory-report-analytics',
            label: 'Analytics',
            icon: null,
            to: '/inventory/reports/analytics',
            requiredControl: AccessControlCode.INVENTORY_REPORTS_VIEW,
          },
        ],
      },
    ],
  },
];

// ----------------------------------------------------------------
// Settings sections
// ----------------------------------------------------------------
export const SETTINGS_SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  {
    id: 'settings-overview',
    links: [
      {
        id: 'settings-dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/settings/dashboard',
        requiredControl: AccessControlCode.SETTINGS_PAGE_ACCESS,
      },
    ],
  },
  {
    id: 'settings-access',
    title: 'Users & Access',
    links: [
      {
        id: 'settings-users',
        label: 'Users',
        icon: Users,
        children: [
          {
            id: 'settings-all-users',
            label: 'All Users',
            icon: null,
            to: '/settings/users',
            requiredControl: AccessControlCode.SETTINGS_USERS_MANAGE,
          },
          {
            id: 'settings-roles',
            label: 'Roles',
            icon: null,
            to: '/settings/roles',
            requiredControl: AccessControlCode.SETTINGS_ROLES_MANAGE,
          },
        ],
      },
      {
        id: 'settings-security',
        label: 'Security',
        icon: ShieldCheck,
        allowedRoles: [UserRole.SUPER, UserRole.SUPER_ADMIN],
        children: [
          {
            id: 'settings-security-access-controls',
            label: 'Access Controls',
            icon: null,
            to: '/settings/security/access-controls',
            requiredControl:
              AccessControlCode.SETTINGS_SECURITY_ACCESS_CONTROLS_VIEW,
          },
          {
            id: 'settings-security-policies',
            label: 'Policies',
            icon: null,
            to: '/settings/security/policies',
            requiredControl:
              AccessControlCode.SETTINGS_SECURITY_POLICIES_MANAGE,
          },
          {
            id: 'settings-security-assignments',
            label: 'Role Assignments',
            icon: null,
            to: '/settings/security/assignments',
            requiredControl:
              AccessControlCode.SETTINGS_SECURITY_ROLE_ASSIGNMENTS_MANAGE,
          },
          {
            id: 'settings-security-registered-devices',
            label: 'Registered Devices',
            icon: null,
            to: '/settings/security/registered-devices',
            requiredControl:
              AccessControlCode.SETTINGS_SECURITY_REGISTERED_DEVICES_MANAGE,
          },
        ],
      },
    ],
  },
  {
    id: 'settings-notifications',
    title: 'Notifications',
    links: [
      {
        id: 'settings-email-configurations',
        label: 'Email Configurations',
        icon: Mail,
        to: '/settings/email-configurations',
        requiredControl: AccessControlCode.SETTINGS_PAGE_ACCESS,
      },
    ],
  },
  {
    id: 'settings-devices',
    title: 'Devices',
    links: [
      {
        id: 'settings-printers',
        label: 'Printer Settings',
        icon: Printer,
        children: [
          {
            id: 'settings-printers-all',
            label: 'All Printers',
            icon: null,
            to: '/settings/printers/all',
            requiredControl: AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
          },
          {
            id: 'settings-printers-configuration',
            label: 'Printer Configuration',
            icon: null,
            to: '/settings/printers/configuration',
            requiredControl: AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
          },
        ],
      },
    ],
  },
  {
    id: 'settings-subscription',
    title: 'Subscription',
    links: [
      {
        id: 'settings-billing',
        label: 'Billing',
        icon: CreditCard,
        to: '/settings/billing',
        requiredControl: AccessControlCode.DASHBOARD_BILLINGS_ACCESS,
      },
    ],
  },
  {
    id: 'settings-fiscal',
    title: 'RRA Fiscal (EBM)',
    links: [
      {
        id: 'settings-ebm',
        label: 'EBM',
        icon: Cpu,
        requiresFiscalIntegration: true,
        children: [
          {
            id: 'settings-ebm-configuration',
            label: 'EBM Configuration',
            icon: null,
            to: '/settings/ebm/configuration',
            requiredControl: AccessControlCode.SETTINGS_EBM_CONFIG_MANAGE,
            requiresFiscalIntegration: true,
          },
          {
            id: 'settings-ebm-missing-fiscal-setup',
            label: 'Missing Fiscal Setup',
            icon: AlertTriangle,
            to: '/settings/ebm/missing-fiscal-setup',
            requiredControl: AccessControlCode.SETTINGS_EBM_FISCAL_SETUP_MANAGE,
            requiresFiscalIntegration: true,
          },
          {
            id: 'settings-ebm-configured-fiscal-setup',
            label: 'Configured Fiscal Setup',
            icon: CheckCircle2,
            to: '/settings/ebm/configured-fiscal-setup',
            requiredControl: AccessControlCode.SETTINGS_EBM_FISCAL_SETUP_MANAGE,
            requiresFiscalIntegration: true,
          },
          {
            id: 'settings-ebm-sale-records',
            label: 'Sale Records',
            icon: RotateCw,
            to: '/settings/ebm/sale-records',
            requiredControl: AccessControlCode.SETTINGS_EBM_SALE_RECORDS_MANAGE,
            requiresFiscalIntegration: true,
          },
        ],
      },
    ],
  },
];
